import {observer} from 'mobx-react-lite'
import React from 'react'
import {useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {TaskDependencyLinkItem} from '@components/right-panel/task-info-log/connected-tasks/task-dependency-link-item'
import {TaskAvailabilityInfo} from '@components/task-availability-info/task-availability-info'
import {taskConnectionStore} from '@components/task-card/task-connection-store'
import {TaskEventLog} from '@components/task-event-log/task-event-log'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {ChatUtils} from '@extensions/chat-utils'
import {Gantt} from '@extensions/gantt'
import {globals} from '@extensions/globals-base'
import {useTaskInfoLogStore} from '../task-info-log-store'

// eslint-disable-next-line max-lines-per-function
export const ConnectedTasks = observer(() => {
  const [state, store] = useTaskInfoLogStore()

  const hub = state.hub
  const forceUpdate = useForceUpdate()
  useSyncEvent('current', {filter: hub?.id}, forceUpdate, [hub?.id])
  useEffect(() => Gantt.subscribe(forceUpdate), [])

  if (!hub) {
    return null
  }

  const allowConnect = store.allowConnect()
  const {useNewGantt} = CompanyPreferencesClient.get(globals.getCompanyId())

  return (
    <>
      <div
        className={cn(
          'task-info__title',
          state.spoilers.availabilityInfo && 'task-info__title--opened'
        )}
        onClick={store.spoilersToggleAvailabilityInfo}
      >
        {Lang.msg.chat.availableToTitle}
      </div>
      {state.spoilers.availabilityInfo && (
        <div className="task-info__cnt">
          <TaskAvailabilityInfo taskId={hub.id} />
        </div>
      )}

      <div
        className={cn(
          'task-info__title',
          state.spoilers.connectedTasks && 'task-info__title--opened'
        )}
        onClick={store.spoilersToggleConnectedTasks}
      >
        {Lang.msg.connectedTasks}
      </div>
      {state.spoilers.connectedTasks && (
        <div className="task-info__cnt">
          {store.listConnectedHubs().map(connectedTask => (
            <div className="chat-connected-item" key={connectedTask.id}>
              {allowConnect && (
                <span
                  className="chat-connected-item-x"
                  title={Lang.msg.clickDisconnect}
                  onClick={() =>
                    store.disconnectTask(connectedTask, 'connection')
                  }
                >
                  {Lang.msg.unlink}
                </span>
              )}

              <span
                className="chat-connected-item-name _link"
                onClick={() => store.openChat(connectedTask)}
              >
                {connectedTask.title}
              </span>
              <div className="chat-connected-item-location">
                {ChatUtils.getChatLocation(connectedTask)}
              </div>
            </div>
          ))}

          {allowConnect && (
            <div className="chat-connected-with">
              <div className="_icon chat-connected-add-icon" />
              <span
                className="_link"
                onClick={() => {
                  globals.taskToConnect = hub
                  globals.taskToConnectType = 'connection'
                  globals.taskToConnectUpdated?.()
                  taskConnectionStore.setData(hub)
                }}
              >
                {Lang.msg.connectTaskWith}
              </span>
            </div>
          )}

          {!!store.listRepeatedTasks().length && (
            <div>
              <div className="task-info__title">
                {Lang.msg.repeat.repeatedTitle}
              </div>
              {store.listRepeatedTasks().map(repeatTask => (
                <div className="chat-connected-item" key={repeatTask.id}>
                  <span
                    className="chat-connected-item-name _link"
                    onClick={() => store.openChat(repeatTask)}
                  >
                    {repeatTask.title}
                  </span>
                  <div className="chat-connected-item-location">
                    {ChatUtils.getChatLocation(repeatTask)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!!store.listDependencies().length && (
        <>
          <div
            className={cn(
              'task-info__title',
              state.spoilers.dependsOnTasks && 'task-info__title--opened'
            )}
            onClick={store.spoilersToggleDependsOnTasks}
          >
            {Lang.msg.dependency.dependsOn}
          </div>

          {state.spoilers.dependsOnTasks && (
            <div className="task-info__cnt">
              {store.listDependencies().map(fromHub => (
                <TaskDependencyLinkItem
                  key={fromHub.id}
                  fromHub={fromHub}
                  toHub={hub}
                  type="dependent"
                />
              ))}

              {allowConnect && !useNewGantt && (
                <div className="chat-connected-with">
                  <div className="_icon chat-connected-add-icon"></div>
                  <span
                    className="_link"
                    onClick={() => {
                      globals.taskToConnect = hub
                      globals.taskToConnectType = 'dependent'
                      globals.taskToConnectUpdated?.()
                      taskConnectionStore.setData(hub)
                    }}
                  >
                    {Lang.msg.dependency.addDependsOn}
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!!store.listDependent().length && (
        <>
          <div
            className={cn(
              'task-info__title',
              state.spoilers.dependentTasks && 'task-info__title--opened'
            )}
            onClick={store.spoilersToggleDependentTasks}
          >
            {Lang.msg.dependency.dependent}
          </div>

          {state.spoilers.dependentTasks && (
            <div className="task-info__cnt">
              {store.listDependent().map(toHub => (
                <TaskDependencyLinkItem
                  key={toHub.id}
                  fromHub={hub}
                  toHub={toHub}
                  type="dependency"
                />
              ))}

              {allowConnect && !useNewGantt && (
                <div className="chat-connected-with">
                  <div className="_icon chat-connected-add-icon" />
                  <span
                    className="_link"
                    onClick={() => {
                      globals.taskToConnect = hub
                      globals.taskToConnectType = 'dependency'
                      globals.taskToConnectUpdated?.()
                      taskConnectionStore.setData(hub)
                    }}
                  >
                    {Lang.msg.dependency.addDependent}
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div
        className={cn(
          'task-info__title',
          state.spoilers.log && 'task-info__title--opened'
        )}
        onClick={store.spoilersToggleLog}
      >
        {Lang.msg.chat.taskEventLog}
      </div>
      {state.spoilers.log && (
        <div className="task-info__cnt">
          <TaskEventLog taskId={hub.id} />
        </div>
      )}
    </>
  )
})
